import {Injectable} from '@angular/core'
import {LogSeverity, LogSeverityLabel} from '@models/log-verbosity'
import {LoggingService} from './logging.service'
import {UveLogKey} from '../models/constants/log-key'
import type {LogEntry} from './logging/logger'

/**
 * Variante de `LoggingService` para la app en modo embebido.
 *
 * @description
 * En el modo embebido (`start-embedded`, puerto 4201) no queremos enviar
 * trazas a Firestore ni tocar Split.io: la app está embebida en otro
 * contenedor/host y la telemetría se maneja por otra vía. Esta subclase
 * sobreescribe todos los puntos de entrada para:
 *
 * - Mapear `severity` a `console.log/warn/error/debug` directamente.
 * - Devolver siempre `true` en las inserciones de traza (no-op con éxito).
 * - Deshabilitar claves "importantes".
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * Reemplaza a `LoggingService` vía provider override en el bootstrap del
 * modo embebido; el resto de servicios siguen inyectando `LoggingService`
 * y obtienen esta implementación.
 *
 * ### Bad Uses / Errores comunes
 *
 * - Usar esta clase fuera del modo embebido: perderemos todas las trazas
 *   remotas sin intención.
 */
@Injectable({
  providedIn: 'root',
})
export class LoggingEmbeddedService extends LoggingService {
  override reload = async (): Promise<void> => {}

  protected override dispatch = async (entry: LogEntry): Promise<void> => {
    const {action, severity, value} = entry
    if (severity === LogSeverity.warning) {
      console.warn('LoggingEmbeddedService log', action, LogSeverityLabel[severity], value)
      return
    }
    if (severity === LogSeverity.error) {
      console.error('LoggingEmbeddedService log', action, LogSeverityLabel[severity], value)
      return
    }
    if (severity === LogSeverity.critical) {
      console.error('LoggingEmbeddedService log', action, LogSeverityLabel[severity], value)
      return
    }
    if (severity === LogSeverity.fatal) {
      console.error('LoggingEmbeddedService log', action, LogSeverityLabel[severity], value)
      return
    }
    if (severity === LogSeverity.info) {
      console.log('LoggingEmbeddedService log', action, LogSeverityLabel[severity], value)
      return
    }
    if (severity === LogSeverity.debug) {
      console.debug('LoggingEmbeddedService log', action, LogSeverityLabel[severity], value)
    }
  }

  protected override normalizeLogValue = (value: unknown): any => value

  protected override safeFormatError = (_input: unknown): {error: string} => ({error: 'Embedded error override'})

  protected override hydrateImportantKeys = async (): Promise<void> => {}

  protected override isImportantKey = (_action: UveLogKey): boolean => false

  protected override getImportantTracesKeys = async (): Promise<string[]> => []

  override insertTrace = async <T>(_action: string, _severity: string, _value: Partial<T>): Promise<boolean> => true
}
